<template>
    <v-expansion-panels v-model="panels">
        <v-expansion-panel>
            <v-expansion-panel-header>
                <div class="expPanelHead">{{ $t('thresholds.title') }} <ThresholdTooltip>{{$t('thresholds.description')}} {{$t(`thresholds.desc-${listType}`)}}</ThresholdTooltip></div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
                <div class="thresholds">
                    <v-card elevation="2">
                        <div class="editPanel">
                            <div v-if="editMode" class="quickActions">
                                <div class="sectionSubTitle">{{$t('EditorPanel.actions.quickActions')}}</div>
                                <div class="ed-actions">
                                    <div class="ed-action-part" v-for="changeValue in changeValues" :key="`${changeValue}-ed`">
                                        <v-btn small outlined rounded color="teal darken-1" dark @click="addToAll(changeValue)"><font-awesome-icon :icon="icons.up" class="fa-fw mr-1"></font-awesome-icon>{{changeValue}}%</v-btn>
                                        <v-btn small outlined rounded color="teal darken-1" dark @click="removeToAll(changeValue)"><font-awesome-icon :icon="icons.down" class="fa-fw mr-1"></font-awesome-icon>{{changeValue}}%</v-btn>
                                    </div>
                                </div>
                            </div>
                            <BuyEditionPanel v-if="listType === 'buy'" :thresholds="thresholds" ref="buyEditionPanel" @isFormValid="subEditFormValid" @inputChanged="editInputChanged"></BuyEditionPanel>
                            <SellEditionPanel v-else :thresholds="thresholds" ref="sellEditionPanel" @isFormValid="subEditFormValid" @inputChanged="editInputChanged"></SellEditionPanel>
                            <div class="actions">
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            fab
                                            small
                                            :dark="saveBtnEnabled"
                                            :disabled="!saveBtnEnabled"
                                            color="primary"
                                            @click="save"
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <font-awesome-icon :icon="icons.save" class="fa-fw fa-lg"></font-awesome-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{$t('EditorPanel.buttons.save')}}</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            fab
                                            small
                                            :dark="saveBtnEnabled"
                                            :disabled="!saveBtnEnabled"
                                            color="secondary"
                                            @click="cancel"
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <font-awesome-icon :icon="icons.cancel" class="fa-fw fa-lg"></font-awesome-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{$t('EditorPanel.buttons.cancel')}}</span>
                                </v-tooltip>
                            </div>
                        </div>
                    </v-card>
                    <Alert ref="iAlert"></Alert>
                </div>
            </v-expansion-panel-content>
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<script>
import { faInfoCircle } from "@fortawesome/pro-duotone-svg-icons";
import { faArrowDown, faArrowUp, faCheck, faCircle, faTimes } from "@fortawesome/pro-solid-svg-icons";
import StockHtmlHelper from "../../stocks/StockHtmlHelper";
import BuyEditionPanel from './BuyEditionPanel'
import SellEditionPanel from './SellEditionPanel'
import { authComputed } from "@/store/helpers/AuthenticationHelper"
import Alert from '@/components/controls/Alert.vue'
import UserListEntry from '../../../models/UserListEntry';
import ThresholdTooltip from './ThresholdTooltip.vue';

export default {
    props: ["stock", "stockListId", "score"],
    data() {
        return {
            icons: {
                up: faArrowUp,
                down: faArrowDown,
                help: faInfoCircle,
                dot: faCircle,
                save: faCheck,
                cancel: faTimes
            },
            panels: undefined,
            changeValues: [1, 2, 5, 10],
            showHelp: false,
            dialog: false,
            dialogLoaded: false,
            itemListId: undefined,
            editMode: true,
            listType: 'buy',
            title: '',
            icon: faCircle,
            stockHtmlHelper: undefined,
            showStockInfos: false,
            showStockLoader: false,
            saveBtnEnabled: false,
            thresholds: [],
            rules: {
                required: value => !!value || this.$t('EditorPanel.errors.required'),
                isSymbol: value => {
                    const pattern = /^([a-zA-Z-0-9.\\-]+)$/;
                    return (
                        pattern.test(value) || !value || this.$t('EditorPanel.errors.invalidSymbol')
                    );
                }
            }
        }
    },
    created() {
        this.stockHtmlHelper = new StockHtmlHelper();
        this.init();
    },
    computed: {
        ...authComputed,
        isDemo() {
            return this.hasRole('demonstration');
        },
        getScoreCalculation() {
            return this.$store.getters.getScoreCalculationBuyListInfos;
        }
    },
    components: {
        BuyEditionPanel,
        SellEditionPanel,
        Alert,
        ThresholdTooltip
    },
    methods: {
        init() {
            this.thresholds = this.stock.getThresholds();
            this.setListTypeFromStock();
            this.reset();
            const dist = this.getScoreCalculation.distribution;
            this.panels = dist.thresholds === 1 ? 0 : undefined;
        },

        addToAll(value) {
            for (const t in this.thresholds) {
                if (Object.hasOwnProperty.call(this.thresholds, t)) {
                    if (this.thresholds[t] !== '') {
                        const tmpT = this.thresholds[t] + (this.thresholds[t] * (value / 100));
                        this.thresholds[t] = this.stockHtmlHelper.normalizePrice(tmpT);
                        this.saveBtnEnabled = true;
                    }
                }
            }
        },

        removeToAll(value) {
            for (const t in this.thresholds) {
                if (Object.hasOwnProperty.call(this.thresholds, t)) {
                    if (this.thresholds[t] !== '') {
                        const tmpT = this.thresholds[t] - (this.thresholds[t] * (value / 100));
                        this.thresholds[t] = this.stockHtmlHelper.normalizePrice(tmpT);
                        this.saveBtnEnabled = true;
                    }
                }
            }
        },

        toggleHelp() {
            this.showHelp = !this.showHelp;
        },

        save() {
            const panel = this.$refs[`${this.listType}EditionPanel`];
            if (panel.isFormValid()) {
                const thresholds = panel.thresholds;
                this.stock.setThresholds(thresholds);
                if (this.$store.getters.stockIsInList(this.stockListId, this.stock.stockId)) {
                    this.updateStock();
                } else {
                    this.addStock();
                }
            }
        },

        updateStock() {
            return new Promise((resolve, reject) => {
                this.$store.dispatch('updateStockInUserWatchList', { listId: this.stockListId, item: this.stock, listType: this.listType }).then(() => {
                    this.reset();
                    resolve()
                }).catch(error => {
                    const errorCode = error[0].code || error[0].errorCode
                    this.$refs.iAlert.error({ errorCode });
                    reject(error)
                });
            })
        },

        addStock() {
            return new Promise((resolve, reject) => {
                const item = this.stock.rawData;
                item.id = undefined;
                this.$store.dispatch('addStockInWatchList', { listId: this.stockListId, item, listType: this.listType }).then(() => {
                    this.reset();
                    resolve()
                }).catch(error => {
                    const errorCode = error[0].code || error[0].errorCode
                    this.$refs.iAlert.error({ errorCode });
                    reject(error)
                });
            })
        },

        cancel() {
            this.thresholds = this.stock.getThresholds();
            this.reset();
        },

        reset() {
            this.saveBtnEnabled = false;
            this.$store.dispatch('setDirtyThresholds', { dirty: false })
        },

        subEditFormValid(status) {
            this.saveBtnEnabled = this.$store.getters.hasDirtyThresholds && status.valid;
        },

        editInputChanged() {
            this.saveBtnEnabled = true
        },

        setListTypeFromStock() {
            this.allLists = Object.keys(this.$store.state.list.lists).reduce((acc, cur) => acc.concat(this.$store.state.list.lists[cur]), []);
            if (this.stockListId === -1) {
                this.listType = this.stock.listType;
            } else {
                this.listType = this.allLists.find(list => list.id === this.stockListId).type;
            }
        }
    },
    watch: {
        stock(val) {
            if (val instanceof UserListEntry) {
                this.init()
            }
        },
        saveBtnEnabled(val) {
            this.$store.dispatch('setDirtyThresholds', { dirty: val })
        }
    }
}
</script>

<style lang="scss" scoped>
@import "../../../assets/styles/breakpoints.scss";
.expPanelHead {
    display: flex;
    align-items: center;
    gap: 10px;
}
.sectionTitle {
    display: flex;
    justify-content: flex-end;
}
.editPanel {
    display: flex;
    align-items: center;
    padding: 0 15px 15px;
    .quickActions {
        display: flex;
        flex-direction: column;
        gap: 10px;
        .ed-actions {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 10px;
            .ed-action-part {
                display: flex;
                gap: 10px;
            }
        }
    }
    .actions {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    @media screen and (max-width: $small){
        flex-direction: column;
        align-items: flex-end;
        gap: 15px;
        .quickActions {
            .ed-actions {
                flex-direction: row;
                flex-wrap: wrap;
                .ed-action-part {
                    flex-direction: column;
                }
            }
        }
        .actions{
            flex: 1;
        }
    }
}
</style>
